import { useState, useEffect, useRef, useCallback } from 'react';
import { geocode } from '../utils/api';
import type { GeocodeResult } from '../types/api';

interface AddressSearchProps {
  label: string;
  value: string;
  coords: { lat: number; lon: number } | null;
  onSelect: (result: GeocodeResult) => void;
  onCoordsChange: (lat: number, lon: number) => void;
  showLocButton?: boolean;
  onLocClick?: () => void;
  userLocation: { lat: number; lon: number } | null;
  placeholder?: string;
}

function shortAddress(displayName: string): string {
  // Keep the full context-rich label the backend now returns; trim only the
  // trailing country if present, since the user is already local.
  const parts = displayName.split(',').map((s: string) => s.trim());
  if (parts.length <= 3) return parts.join(', ');
  // Drop a trailing country ("USA"/"United States") when present.
  const last = parts[parts.length - 1].toLowerCase();
  if (last === 'usa' || last === 'united states' || last === 'us') {
    return parts.slice(0, -1).join(', ');
  }
  return parts.join(', ');
}

function dropdownLabel(displayName: string): string {
  // The backend now returns labels that always include locality context
  // (city/county/state), so "Main Street" alone never appears. Show the full
  // label so the user can tell two same-named streets apart. Cap length so
  // the dropdown row stays readable.
  const label = shortAddress(displayName);
  return label.length > 64 ? label.slice(0, 61) + '…' : label;
}

// Split the display name into a primary (business/POI name or street address)
// and a secondary (the address/locality context). Shown in the dropdown as a
// bold primary line with a smaller secondary line beneath it.
function splitLabel(displayName: string): { primary: string; secondary: string } {
  const parts = shortAddress(displayName).split(',').map((s: string) => s.trim()).filter((s) => s.length > 0);
  if (parts.length === 0) return { primary: '', secondary: '' };
  if (parts.length === 1) return { primary: parts[0], secondary: '' };
  return { primary: parts[0], secondary: parts.slice(1).join(', ') };
}

export function AddressSearch({
  label,
  value,
  coords,
  onSelect,
  onCoordsChange,
  showLocButton,
  onLocClick,
  userLocation,
  placeholder = 'Search address or enter lat, lon',
}: AddressSearchProps) {
  const [query, setQuery] = useState(value);
  const [results, setResults] = useState<GeocodeResult[]>([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [searching, setSearching] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setQuery(value);
  }, [value]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSearch = useCallback((text: string) => {
    setQuery(text);
    if (debounceRef.current) clearTimeout(debounceRef.current);

    const coordMatch = text.match(/^\s*(-?\d+\.?\d*)\s*,\s*(-?\d+\.?\d*)\s*$/);
    if (coordMatch) {
      const lat = parseFloat(coordMatch[1]);
      const lon = parseFloat(coordMatch[2]);
      if (!isNaN(lat) && !isNaN(lon) && lat >= -90 && lat <= 90 && lon >= -180 && lon <= 180) {
        onCoordsChange(lat, lon);
        setResults([]);
        setShowDropdown(false);
        return;
      }
    }

    if (text.trim().length < 3) {
      setResults([]);
      setShowDropdown(false);
      return;
    }

    debounceRef.current = setTimeout(async () => {
      setSearching(true);
      try {
        const r = await geocode(text, 8, userLocation);
        setResults(r);
        setShowDropdown(r.length > 0);
      } catch {
        setResults([]);
      }
      setSearching(false);
    }, 400);
  }, [onCoordsChange, userLocation]);

  const handleSelect = (result: GeocodeResult) => {
    setQuery(shortAddress(result.display_name));
    setShowDropdown(false);
    onSelect(result);
  };

  return (
    <div className="input-group" ref={containerRef} style={{ position: 'relative' }}>
      <label>{label}</label>
      <div style={{ display: 'flex', gap: 4, width: '100%' }}>
        <div style={{ position: 'relative', flex: 1, minWidth: 0 }}>
          <input
            type="text"
            value={query}
            onChange={(e) => handleSearch(e.target.value)}
            onFocus={() => { if (results.length > 0) setShowDropdown(true); }}
            placeholder={placeholder}
            style={{ width: '100%', paddingRight: 28, boxSizing: 'border-box' }}
          />
          {searching && (
            <span style={{
              position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)',
              fontSize: 12, color: '#e94560',
            }}>...</span>
          )}
        </div>
        {showLocButton && (
          <button
            onClick={onLocClick}
            title="Use my location"
            style={{
              flexShrink: 0,
              padding: '2px 6px',
              fontSize: 16,
              border: '1px solid #0f3460',
              borderRadius: 3,
              background: '#0f3460',
              cursor: 'pointer',
              lineHeight: 'normal',
            }}
          >
            📍
          </button>
        )}
      </div>
      {value && (
        <span style={{ fontSize: 10, color: '#6bcb77' }}>
          {value}
        </span>
      )}
      {showDropdown && results.length > 0 && (
        <ul style={{
          position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 100,
          listStyle: 'none', margin: 0, padding: 0,
          background: '#16213e', border: '1px solid #0f3460',
          borderRadius: 4, maxHeight: 200, overflowY: 'auto',
          boxShadow: '0 4px 12px rgba(0,0,0,0.5)',
        }}>
          {results.map((r, i) => {
            const { primary, secondary } = splitLabel(r.display_name);
            return (
              <li
                key={i}
                onClick={() => handleSelect(r)}
                style={{
                  padding: '8px 10px', cursor: 'pointer',
                  borderBottom: i < results.length - 1 ? '1px solid #0f3460' : 'none',
                  color: '#e0e0e0',
                  lineHeight: 1.3,
                  whiteSpace: 'normal',
                  wordBreak: 'break-word',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.background = '#1a1a2e'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
              >
                <div style={{ fontSize: 13, fontWeight: 600 }}>{primary}</div>
                {secondary && (
                  <div style={{ fontSize: 10, color: '#a0a0c0', marginTop: 1 }}>{secondary}</div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
